import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Lock,
  Phone,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Camera,
  Smartphone,
  RotateCcw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { clientSignUp } from '../lib/clientAuthService';
import { sendRegistrationOtpRequest, verifyRegistrationOtpRequest } from '../lib/otpService';
import { PairUpLogo } from '../components/common/PairUpLogo';
import { DateOfBirthInput } from '../components/common/DateOfBirthInput';
import { validateAdultDob } from '../utils/dobUtils';

const AVATAR_PRESETS = [
  'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80',
  'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?auto=format&fit=crop&w=400&q=80',
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
];

export const ClientSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [fullName, setFullName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gender, setGender] = useState<'Male' | 'Female'>('Male');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('1998-05-14');
  const [dobError, setDobError] = useState<string | null>(null);
  const [city, setCity] = useState('Kolkata, West Bengal');
  const [bio, setBio] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(AVATAR_PRESETS[0]);
  const [agreedTerms, setAgreedTerms] = useState(false);

  // Registration Mobile OTP Verification State
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [otpVerificationId, setOtpVerificationId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMaskedDestination, setOtpMaskedDestination] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  const validateRegistrationFields = (): string | null => {
    if (!fullName.trim() || fullName.trim().length < 2) {
      return 'Please enter your full legal name.';
    }
    if (!gender || (gender !== 'Male' && gender !== 'Female')) {
      return 'Please select your gender (Male or Female).';
    }
    if (!email.trim() || !email.includes('@')) {
      return 'Please enter a valid email address.';
    }
    const cleanPhone = phone.trim().replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10 || !/^[6-9]\d{9}$/.test(cleanPhone.slice(-10))) {
      return 'Please enter a valid 10-digit Indian mobile number.';
    }
    const dobCheck = validateAdultDob(dob, new Date(), gender);
    if (!dobCheck.valid) {
      setDobError(dobCheck.error || 'Not eligible — your age does not meet the minimum requirement.');
      return dobCheck.error || 'Not eligible — your age does not meet the minimum requirement.';
    }
    setDobError(null);
    if (!password || password.length < 6) {
      return 'Password must be at least 6 characters long.';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }
    if (!agreedTerms) {
      return 'Please agree to the Terms of Service and Privacy Policy to continue.';
    }
    return null;
  };

  const handleRequestOtp = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);
    const fieldErr = validateRegistrationFields();
    if (fieldErr) {
      setErrorMsg(fieldErr);
      return;
    }

    const cleanPhone = phone.trim().replace(/\D/g, '').slice(-10);
    setIsLoading(true);
    try {
      const otpRes = await sendRegistrationOtpRequest(cleanPhone);
      if (!otpRes.success || !otpRes.verificationId) {
        setErrorMsg(otpRes.message || 'Could not send OTP to your mobile number.');
        return;
      }
      setOtpVerificationId(otpRes.verificationId);
      setOtpMaskedDestination(otpRes.destinationMasked || `+91 ******${cleanPhone.slice(-4)}`);
      setOtpCooldown(otpRes.cooldownSeconds || 60);
      setIsOtpStep(true);
      setSuccessMsg(otpRes.message || 'Verification code sent! Enter the 6-digit SMS code below.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    const fieldErr = validateRegistrationFields();
    if (fieldErr) {
      setErrorMsg(fieldErr);
      return;
    }

    if (!isOtpStep && !isOtpVerified) {
      await handleRequestOtp();
      return;
    }

    const cleanPhone = phone.trim().replace(/\D/g, '').slice(-10);
    const dobCheck = validateAdultDob(dob, new Date(), gender);

    setIsLoading(true);
    try {
      if (!isOtpVerified) {
        const cleanOtp = otpCode.trim().replace(/\D/g, '');
        if (cleanOtp.length !== 6) {
          setErrorMsg('Please enter the 6-digit verification code sent to your mobile.');
          setIsLoading(false);
          return;
        }
        const verifyRes = await verifyRegistrationOtpRequest(cleanPhone, otpVerificationId, cleanOtp);
        if (!verifyRes.success) {
          setErrorMsg(verifyRes.message || 'Invalid or expired OTP code.');
          setIsLoading(false);
          return;
        }
        setIsOtpVerified(true);
      }

      const { profile } = await clientSignUp({
        email,
        password,
        fullName: fullName.trim(),
        displayName: displayName.trim() || fullName.trim().split(' ')[0],
        phone: cleanPhone,
        gender,
        dob: dobCheck.normalizedDob,
        city: city.trim(),
        bio: bio.trim(),
        avatarUrl,
      });

      // Update AuthContext session with authenticated profile
      login(profile.displayName, 'client', {
        ...profile,
        name: profile.displayName,
        role: 'Verified Client',
      });

      setSuccessMsg(`Welcome to PairUpToday, ${profile.displayName}! Proceeding to Document Verification (Step 2 of 2)...`);

      setTimeout(() => {
        navigate('/kyc-verification?from=client-signup', { replace: true });
      }, 700);
    } catch (err: any) {
      let msg = err.message || 'Failed to create client account.';
      if (err.code === 'auth/email-already-in-use') {
        msg = 'This email address is already registered. Please log in instead.';
      } else if (err.code === 'auth/weak-password') {
        msg = 'The password is too weak. Please use at least 6 characters.';
      } else if (err.code === 'auth/invalid-email') {
        msg = 'Invalid email address format.';
      }
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col justify-between selection:bg-pink-500 selection:text-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <PairUpLogo className="w-9 h-9 shrink-0 drop-shadow-xs" />
          <div className="flex flex-col">
            <span className="text-xl font-black tracking-tight text-slate-900 leading-none">
              PairUp<span className="text-pink-600">Today</span>
            </span>
            <span className="text-[10px] font-semibold text-slate-400">Client Portal</span>
          </div>
        </Link>

        <div className="flex items-center gap-2 text-xs font-bold">
          <span className="text-slate-500 hidden sm:inline">Already registered?</span>
          <Link
            to="/login"
            className="px-3.5 py-1.5 bg-pink-50 hover:bg-pink-100 text-pink-600 border border-pink-200 rounded-full transition-colors cursor-pointer"
          >
            Log In
          </Link>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 my-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl max-w-xl w-full p-6 sm:p-8 space-y-6">
          {/* Header Title */}
          <div className="text-center space-y-1.5">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-pink-50 border border-pink-200 rounded-full text-pink-700 text-[11px] font-black uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-pink-600" />
              <span>Free Client Registration</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Create Client Account
            </h1>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Register once to receive your permanent <strong>Client ID</strong> and automatically access nearby companion bookings.
            </p>
          </div>

          {/* Feedback Banners */}
          {errorMsg && (
            <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-xs font-bold text-rose-800 flex items-start gap-2.5 shadow-xs">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
              <div className="flex-1">
                <span>{errorMsg}</span>
              </div>
            </div>
          )}

          {successMsg && (
            <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-800 flex items-center gap-2.5 shadow-xs">
              <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Avatar Selector */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center gap-4">
              <div className="relative">
                <img
                  src={avatarUrl}
                  alt="Profile Avatar"
                  className="w-14 h-14 rounded-2xl object-cover border-2 border-pink-500 shadow-xs"
                />
                <span className="absolute -bottom-1 -right-1 bg-pink-600 text-white p-1 rounded-full text-[10px]">
                  <Camera className="w-2.5 h-2.5" />
                </span>
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-slate-800 block">Choose Profile Avatar</span>
                <div className="flex gap-1.5 mt-1.5 overflow-x-auto pb-1">
                  {AVATAR_PRESETS.map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setAvatarUrl(preset)}
                      className={`w-7 h-7 rounded-lg overflow-hidden border-2 transition-all cursor-pointer shrink-0 ${
                        avatarUrl === preset ? 'border-pink-600 scale-110 shadow-xs' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={preset} alt={`Preset ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Full Legal Name <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    required
                    placeholder="e.g. Vikram Sharma"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                  <User className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Display / Profile Name (Public)
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    placeholder="e.g. Vikram"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                  <Sparkles className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Gender Selection (Male 21+ / Female 18+) */}
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">
                Gender <span className="text-rose-500">*</span>{' '}
                <span className="text-[11px] font-medium text-slate-500">
                  (Minimum age: Male 21+ yrs, Female 18+ yrs)
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {(['Male', 'Female'] as const).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => {
                      setGender(g);
                      setDobError(null);
                    }}
                    className={`py-2.5 px-4 rounded-xl border text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
                      gender === g
                        ? 'bg-pink-50 border-pink-500 text-pink-700 shadow-xs'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <span>{g}</span>
                    <span className="text-[10px] font-semibold opacity-75">
                      ({g === 'Male' ? '21+' : '18+'})
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Email Address <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type="email"
                    inputMode="email"
                    enterKeyHint="next"
                    autoComplete="email"
                    required
                    placeholder="vikram@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-800">
                    10-Digit Mobile Number <span className="text-rose-500">*</span>
                  </label>
                  {isOtpVerified && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <div className="relative flex items-center gap-1.5">
                  <div className="relative flex-1 flex items-center">
                    <span className="absolute left-3 text-xs font-bold text-slate-500 font-mono select-none">
                      +91
                    </span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      enterKeyHint="next"
                      autoComplete="tel"
                      required
                      maxLength={10}
                      placeholder="9876543210"
                      value={phone}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhone(next);
                        if (isOtpStep || isOtpVerified) {
                          setIsOtpStep(false);
                          setIsOtpVerified(false);
                          setOtpCode('');
                        }
                      }}
                      className="w-full pl-12 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                    />
                  </div>
                  {!isOtpVerified && (
                    <button
                      type="button"
                      disabled={isLoading || phone.replace(/\D/g, '').length !== 10}
                      onClick={handleRequestOtp}
                      className="px-3 py-2.5 bg-pink-600 hover:bg-pink-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-extrabold rounded-xl transition-all shrink-0 cursor-pointer"
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Date of Birth Selection (Male 21+ / Female 18+ Enforced) */}
            <DateOfBirthInput
              id="client-signup-dob"
              value={dob}
              gender={gender}
              label={`Date of Birth (${gender === 'Male' ? 'Must be 21+ yrs' : 'Must be 18+ yrs'})`}
              error={dobError}
              onChange={(nextDob) => {
                setDob(nextDob);
                setDobError(null);
              }}
              variant="light"
            />

            {/* City Selection */}
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">
                Primary City / Area <span className="text-rose-500">*</span>
              </label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  required
                  placeholder="e.g. Kolkata, West Bengal"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                />
                <MapPin className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Create Password (min. 6 chars) <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 text-slate-400 hover:text-slate-700 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Confirm Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Optional Bio */}
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">
                Bio / Companion Preferences (Optional)
              </label>
              <textarea
                rows={2}
                placeholder="Share your meeting interests, e.g. cafe dates, family weddings, polite conversational companion..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white resize-none"
              />
            </div>

            {/* Terms Checkbox */}
            <label className="flex items-start gap-2.5 cursor-pointer pt-1">
              <input
                type="checkbox"
                required
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-pink-600 focus:ring-pink-500 w-4 h-4 cursor-pointer"
              />
              <span className="text-[11px] text-slate-600 leading-relaxed select-none">
                I agree to the PairUpToday <strong>Terms of Service</strong> and <strong>Safety & Privacy Policies</strong>. I confirm I am 18+ years of age.
              </span>
            </label>

            {/* Mobile OTP Verification Step */}
            {isOtpStep && !isOtpVerified && (
              <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-200 space-y-3 animate-in fade-in">
                <div className="flex items-center justify-between">
                  <label htmlFor="client-signup-otp" className="text-xs font-extrabold text-indigo-900 flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4 text-indigo-600" />
                    <span>Enter 6-Digit SMS OTP ({otpMaskedDestination})</span>
                  </label>
                  <button
                    type="button"
                    disabled={otpCooldown > 0 || isLoading}
                    onClick={handleRequestOtp}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 hover:text-indigo-900 disabled:text-slate-400 cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>{otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend OTP'}</span>
                  </button>
                </div>
                <input
                  id="client-signup-otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit OTP code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-2.5 bg-white border border-indigo-300 rounded-xl text-sm font-mono font-extrabold tracking-widest text-center text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
            >
              {isLoading ? (
                <span>{isOtpStep ? 'Verifying OTP & Creating Account...' : 'Sending Verification OTP...'}</span>
              ) : isOtpStep && !isOtpVerified ? (
                <>
                  <span>Verify OTP & Create Account</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span>Send OTP & Verify Mobile</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Privacy Security Note */}
          <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 flex items-center gap-2.5 text-[11px] text-slate-500">
            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>
              Your personal contact details and government ID are strictly confidential. Companions only see your approved display name and meetup requests.
            </span>
          </div>

          {/* Switch to Companion */}
          <div className="text-center pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              Want to earn as a Companion Host?{' '}
              <Link to="/companion/register" className="font-bold text-pink-600 hover:text-pink-700 underline">
                Register as Companion
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-slate-400 border-t border-slate-200">
        PairUpToday Secure Client Registration • Firebase Authentication & Cloud Firestore
      </footer>
    </div>
  );
};

export default ClientSignupPage;
